import React, { useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface Particle {
  theta: number;
  phi: number;
  baseRadius: number;
  velocity: { theta: number; phi: number };
  size: number;
  baseOpacity: number;
  color: string;
}

interface InteractiveParticleOrbProps {
  isAnimating?: boolean;
  cursorPosition?: { x: number; y: number } | null;
}

const InteractiveParticleOrb: React.FC<InteractiveParticleOrbProps> = ({ 
  isAnimating = false,
  cursorPosition = null
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();
  const timeRef = useRef(0);
  const { theme } = useTheme();

  // Initialize particles with theme-aware colors
  useEffect(() => {
    const particleCount = 600;
    const baseRadius = 150;
    const particles: Particle[] = [];
    const isDark = theme === 'dark';

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        theta: Math.random() * Math.PI * 2,
        phi: Math.acos(2 * Math.random() - 1),
        baseRadius,
        velocity: {
          theta: (Math.random() - 0.5) * 0.005,
          phi: (Math.random() - 0.5) * 0.005
        },
        size: Math.random() * 2.5 + 1,
        // Brighter in dark mode, darker in light mode
        baseOpacity: isDark 
          ? (Math.random() * 0.4 + 0.6) // 0.6-1.0 for dark mode
          : (Math.random() * 0.5 + 0.5), // 0.5-1.0 for light mode
        color: isDark 
          ? (Math.random() > 0.6 ? '#FFFFFF' : '#CCCCCC') // Light particles for dark mode
          : (Math.random() > 0.6 ? '#111111' : '#333333') // Dark particles for light mode
      });
    }

    particlesRef.current = particles;
  }, [theme]);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      timeRef.current += 0.016;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      particlesRef.current.forEach((particle) => {
        let radius = particle.baseRadius;
        
        // Pulse effect when AI is talking
        if (isAnimating) {
          const pulse = Math.sin(timeRef.current * 4) * 20;
          radius += pulse;
        }

        // Natural rotation
        particle.theta += particle.velocity.theta;
        particle.phi += particle.velocity.phi;

        // Gentle cursor attraction with dead zone
        if (cursorPosition) {
          const dx = cursorPosition.x;
          const dy = cursorPosition.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Only apply attraction if cursor is reasonably close and moving
          if (distance < 300 && distance > 50) {
            const force = (300 - distance) / 300;
            const angle = Math.atan2(dy, dx);
            
            // Much gentler attraction
            particle.theta += Math.cos(angle) * force * 0.05;
            particle.phi += Math.sin(angle) * force * 0.05;
          }
        }

        // Convert spherical to cartesian coordinates
        const x = radius * Math.sin(particle.phi) * Math.cos(particle.theta);
        const y = radius * Math.sin(particle.phi) * Math.sin(particle.theta);
        const z = radius * Math.cos(particle.phi);

        // Project 3D to 2D
        const scale = 500 / (500 + z);
        const x2d = centerX + x * scale;
        const y2d = centerY + y * scale;
        
        // Calculate opacity based on z-depth
        const depthOpacity = (z + radius) / (2 * radius);
        const opacity = particle.baseOpacity * depthOpacity;

        // Draw particle
        ctx.beginPath();
        ctx.arc(x2d, y2d, particle.size * scale, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.globalAlpha = opacity;
        ctx.fill();

        // Add glow when animating
        if (isAnimating) {
          ctx.shadowBlur = 8 * scale;
          ctx.shadowColor = particle.color;
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      });

      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimating, cursorPosition, theme]);

  return (
    <div 
      className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[500px] md:h-[500px] pointer-events-none transition-all duration-500 ${
        isAnimating ? 'top-[25%] md:top-[35%]' : 'top-[calc(25%-2cm)] md:top-[calc(35%-2cm)]'
      }`}
    >
      {/* Canvas for particles */}
      <canvas
        ref={canvasRef}
        width={500}
        height={500}
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
};

export default InteractiveParticleOrb;